import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { useWindowSize } from '@uidotdev/usehooks';
import { Alert, Button, List } from '@pella/sash-framework';
import { formatPhoneNumber } from '../../../utilities/functions';
import { pellaLatitude, pellaLongitude } from '../../../utilities/constants';
import { isValidArray } from '../../../utilities/validations';

import LoadingOverlay from '../../../common/loading-overlay';

import './index.css';

const NearbyShowrooms = ({ locations, error, className, loadMore, ...other }) => {
	const contentQuery = useStaticQuery(query);
	const showroomUrls = contentQuery.allContentstackBranchShowroom.nodes;
	const [locationsSplit, setLocationsSplit] = useState(loadMore ? 2 : locations?.length);
	const { width: windowWidth } = useWindowSize();

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY,
	});

	const center = {
		lat: locations?.[0]?.location?.coordinates?.[1] || pellaLatitude,
		lng: locations?.[0]?.location?.coordinates?.[0] || pellaLongitude,
	};

	const onLoadMoreClick = () => setLocationsSplit(locationsSplit + 2);

	return (
		<div
			className={`nearbyShowrooms flex flex-row flex-wrap max-md:gap-y-10 md:gap-x-6 xl:gap-x-10 ${className || ''}`}
			{...other}
		>
			<div className='nearbyShowrooms-map relative h-[300px] flex-12/12 md:h-[400px] md:flex-1 xl:h-[505px]'>
				{!isLoaded && <LoadingOverlay fixed={false} />}

				{isLoaded && (
					<GoogleMap
						key={locations?.length}
						mapContainerClassName='h-full w-full [&>div>div]:!w-full [&>div>div]:!h-full [&>div>div>img]:object-cover [&>div>div>img]:!h-full'
						options={{
							mapTypeControl: false,
							streetViewControl: false,
							fullscreenControl: false,
						}}
						zoom={locations?.length <= 1 ? 10 : null}
						center={locations?.length <= 1 ? center : null}
						onLoad={(map) => {
							const bounds = new window.google.maps.LatLngBounds();
							if (locations?.length > 1) {
								for (let i = 0; i < locations.length; i++) {
									bounds.extend({
										lat: locations[i].location.coordinates[1],
										lng: locations[i].location.coordinates[0],
									});
								}
								map.fitBounds(bounds);
							}
						}}
					>
						{isValidArray(locations) &&
							locations.map((location, index) => {
								const showroomLink = showroomUrls?.find((url) => url.showroom_id === location.store_number)?.url;

								if (!showroomLink) return;

								return (
									<Marker
										key={index}
										name={location.name}
										position={{
											lat: location.location.coordinates[1],
											lng: location.location.coordinates[0],
										}}
										icon='https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt5d51af0d3a33260d/63615b77ff7b405f6b3f58e9/pella-logo-black-spot.png?width=50'
									/>
								);
							})}
					</GoogleMap>
				)}
			</div>

			<div className='nearbyShowrooms-list flex-12/12 md:flex-1 xl:h-[505px] xl:flex-[0_0_370px] xl:overflow-y-auto'>
				{error != null && (
					<Alert
						type='error'
						hideClose
					>
						{error}
					</Alert>
				)}

				{isValidArray(locations) && (
					<>
						<List
							bullets='none'
							className='space-y-6'
						>
							{locations.slice(0, windowWidth >= 1280 ? locations.length : locationsSplit).map((location, index) => {
								const showroomLink = showroomUrls?.find((url) => url.showroom_id === location.store_number)?.url;
								const showroomName = showroomUrls?.find((url) => url.showroom_id === location.store_number)?.page_heading?.replace(/<\/?span[^>]*>/g, '') || location.name;

								if (!showroomLink) return;

								return (
									<li
										key={index}
										className='nearbyShowrooms-list-item'
									>
										{showroomName && (
											<p className='text-base font-semibold !leading-tight tracking-wider xl:text-xl'>
												{showroomLink ? (
													<Link
														to={showroomLink}
														className='decoration-current underline-offset-auto'
													>
														{showroomName}
													</Link>
												) : (
													showroomName
												)}
											</p>
										)}

										<p className='mb-1 mt-2 text-base leading-tight'>
											<span className='block'>{location.line_1}</span>
											{location.line_2 ? <span className='block'>{location.line_2}</span> : ''}
											<span className='block'>
												{location.city}, {location.state} {location.postal_code}
											</span>
										</p>
										{location.phone && (
											<p className='my-1.5 text-base leading-tight'>
												<span className='mr-1 inline-block w-[70px] whitespace-nowrap text-left font-semibold'>Call Now:</span>
												<a href={`tel:${location.phone}`}>{formatPhoneNumber(location.phone)}</a>
											</p>
										)}
										{location.service_number && (
											<p className='my-1.5 text-base leading-tight'>
												<span className='mr-1 inline-block w-[70px] whitespace-nowrap text-left font-semibold'>Service:</span>
												<a href={`tel:${location.service_number}`}>{formatPhoneNumber(location.service_number)}</a>
											</p>
										)}
									</li>
								);
							})}
						</List>

						{loadMore && locationsSplit < locations?.length && (
							<Button
								onClick={onLoadMoreClick}
								color='secondary'
								className='mt-5 xl:hidden'
								size='sm'
								wide
							>
								Load More
							</Button>
						)}
					</>
				)}
			</div>
		</div>
	);
};

NearbyShowrooms.propTypes = {
	locations: PropTypes.array,
	className: PropTypes.string,
	error: PropTypes.string,
	loadMore: PropTypes.bool,
};

export default NearbyShowrooms;

export const query = graphql`
	query {
		allContentstackBranchShowroom {
			nodes {
				url
				showroom_id
				page_heading
			}
		}
	}
`;
